import { toLocalYMD } from './dateUtils';
import { searchItems } from './searchUtils';

function formatTimePeriods(periods) {
  if (!periods?.length) return '';
  return periods.map((p) => String(p).replace(/_/g, ' ')).join(' ');
}

/** Fields scanned by text search for personal and club hunt logs. */
export const HUNT_LOG_SEARCH_FIELDS = [
  (h) => h.date,
  (h) => h.location,
  (h) => h.notes,
  (h) => formatTimePeriods(h.time_periods),
  (h) => h.does_seen,
  (h) => h.bucks_seen,
  (h) => h.fawns_seen,
  (h) => (h.harvest ? 'harvest harvested' : ''),
  (h) => (h.shot_taken ? 'shot' : ''),
];

/** Scope chip ids for My Hunts filter panel (handoff). */
export const HUNT_SCOPE_ALL = 'all';
export const HUNT_SCOPE_HARVEST = 'harvest';
export const HUNT_SCOPE_BLANK = 'blank';
export const HUNT_SCOPE_CLUB = 'club';
export const HUNT_SCOPE_PRIVATE = 'private';

export const HUNT_SCOPE_CHIPS = [
  { id: HUNT_SCOPE_ALL, label: 'All sits' },
  { id: HUNT_SCOPE_HARVEST, label: 'Harvests' },
  { id: HUNT_SCOPE_BLANK, label: 'Nothing seen' },
  { id: HUNT_SCOPE_CLUB, label: 'Club ground' },
  { id: HUNT_SCOPE_PRIVATE, label: 'My ground' },
];

/**
 * Distinct calendar years present in hunt rows (newest first).
 * @param {Array<{ date?: string }>} hunts
 * @returns {string[]}
 */
export function getHuntYears(hunts) {
  const years = new Set();
  (Array.isArray(hunts) ? hunts : []).forEach((h) => {
    const ymd = toLocalYMD(h?.date);
    if (ymd.length >= 4) years.add(ymd.slice(0, 4));
  });
  return Array.from(years).sort((a, b) => Number(b) - Number(a));
}

/**
 * Filter hunts by calendar year and/or inclusive YYYY-MM-DD range.
 * Year filter is ignored when both startDate and endDate are omitted and year is empty.
 */
export function filterHuntsByDate(hunts, { year = '', startDate = '', endDate = '' } = {}) {
  if (!Array.isArray(hunts)) return [];

  let start = startDate ? toLocalYMD(startDate) : '';
  let end = endDate ? toLocalYMD(endDate) : '';

  if (year) {
    start = `${year}-01-01`;
    end = `${year}-12-31`;
  }

  if (!start && !end) return hunts;

  if (start && end && start > end) {
    [start, end] = [end, start];
  }

  return hunts.filter((h) => {
    const d = toLocalYMD(h?.date);
    if (!d) return false;
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  });
}

/** @param {object} hunt */
export function huntDeerSeenCount(hunt) {
  if (hunt?.total_deer_seen != null) return Number(hunt.total_deer_seen) || 0;
  return (
    (Number(hunt?.does_seen) || 0)
    + (Number(hunt?.bucks_seen) || 0)
    + (Number(hunt?.fawns_seen) || 0)
  );
}

/** Club ground when `club` is a non-null id or object. */
export function huntIsClubGround(hunt) {
  const club = hunt?.club;
  if (club == null || club === '') return false;
  if (typeof club === 'object') return club.id != null;
  return true;
}

/**
 * Apply handoff scope chips (harvest / blank / club / private).
 * @param {Array} hunts
 * @param {string} scope
 */
export function filterHuntsByScope(hunts, scope = HUNT_SCOPE_ALL) {
  if (!Array.isArray(hunts)) return [];
  if (!scope || scope === HUNT_SCOPE_ALL) return hunts;

  return hunts.filter((h) => {
    if (scope === HUNT_SCOPE_HARVEST) return Boolean(h?.harvest);
    if (scope === HUNT_SCOPE_BLANK) return huntDeerSeenCount(h) === 0;
    if (scope === HUNT_SCOPE_CLUB) return huntIsClubGround(h);
    if (scope === HUNT_SCOPE_PRIVATE) return !huntIsClubGround(h);
    return true;
  });
}

/**
 * Apply date filters, scope chip, then tokenized text search.
 */
export function filterHuntLogs(
  hunts,
  { query = '', year = '', startDate = '', endDate = '', scope = HUNT_SCOPE_ALL } = {},
) {
  const byDate = filterHuntsByDate(hunts, { year, startDate, endDate });
  const byScope = filterHuntsByScope(byDate, scope);
  return searchItems(byScope, query, { fields: HUNT_LOG_SEARCH_FIELDS });
}

export function huntLogFiltersActive({
  query = '',
  year = '',
  startDate = '',
  endDate = '',
  scope = HUNT_SCOPE_ALL,
} = {}) {
  return Boolean(
    (query && query.trim())
    || year
    || startDate
    || endDate
    || (scope && scope !== HUNT_SCOPE_ALL),
  );
}
